import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { Socket } from 'ngx-socket-io';
import { EMPTY, merge, of, Subscription } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Fiscal, FiscalSessionChange } from 'src/typings';
import { AppState } from '../redux/app.state';
import { updateFiscalSession } from '../redux/actions/user.actions';

function isSessionChange(value: any): value is FiscalSessionChange {
  return Number.isInteger(value?.fiscalId) && value.fiscalId > 0 &&
    typeof value.isSessionOpen === 'boolean';
}

@Injectable({ providedIn: 'root' })
export class FiscalSessionSyncService implements OnDestroy {
  private subscription?: Subscription;

  constructor(private store: Store<AppState>, private http: HttpClient, private socket: Socket) {}

  start(): void {
    if (this.subscription && !this.subscription.closed) return;
    this.subscription = this.store.select((state) => state.user.info).pipe(
      map((user) => ({ userId: user?.id, fiscalId: user?.fiscal?.id })),
      distinctUntilChanged((a, b) => a.userId === b.userId && a.fiscalId === b.fiscalId),
      switchMap(({ userId, fiscalId }) => {
        if (!userId || !fiscalId) return EMPTY;
        return merge(
          of(null), // Also synchronize when login or the selected register changes.
          this.socket.fromEvent('connect').pipe(map(() => null)),
          this.socket.fromEvent<unknown>('sessionChanges').pipe(
            filter((event) => event === 'sessionChanges' ||
              (isSessionChange(event) && event.fiscalId === fiscalId))
          )
        ).pipe(
          // A newer socket event cancels an older HTTP snapshot. Account/register
          // changes cancel this whole stream, so stale results cannot update them.
          switchMap((event) => isSessionChange(event) ? of(event) :
            this.http.get<Fiscal>(`${environment.BACKEND}/fiscal/${fiscalId}`).pipe(
              filter((fiscal) => fiscal.id === fiscalId && typeof fiscal.isSessionOpen === 'boolean'),
              map((fiscal) => ({ fiscalId, isSessionOpen: fiscal.isSessionOpen })),
              catchError(() => EMPTY)
            )),
          map((state) => updateFiscalSession({ userId, ...state }))
        );
      })
    ).subscribe((action) => this.store.dispatch(action));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }
}
